// ============================================================================
// RemoveNeverStartedLineDialog — a line that never started is removed, not
// stopped.
//
// ⛔ BOTH ANSWERS THE STOP DIALOG OFFERS ARE FALSE HERE. Stopping a line asks
// for the last night and a CHECK refuses a date before the first night. On a
// building that has not opened you can either name a night — claiming we
// cleaned it — or say the night is unknown, which marks the line finished with
// the date missing, and THAT is the signal Finance uses to chase real unpaid
// work. A setup mistake became a false chase.
//
// ⛔ No new status word. Active, Scheduled and Ended are what the line statuses
// mean, plus four legacy words, and none of them means "this never happened".
// Inventing a fifth would be a status nobody else's code knows about. The row
// is removed through the deletion register, which keeps the whole row and the
// reason, so it is recoverable.
//
// ⛔ The reason is MANDATORY server-side and asked for here. Once the line is
// gone the reason is the only record that it was ever there.
// ============================================================================

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button, useToast } from '../../../shared/components';
import { supabase } from '../../../shared/supabase';
import { errorMessage } from '../../../shared/errors';

export function RemoveNeverStartedLineDialog({
  open,
  assignmentId,
  label,
  onClose,
  onRemoved,
}: {
  open: boolean;
  assignmentId: string;
  label: string;
  onClose: () => void;
  onRemoved: () => void;
}) {
  const toast = useToast();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      // ⛔ The refusals live in the database, not here: it refuses once the
      // building has opened, refuses when any money has touched the line, and
      // refuses when the building has ever been invoiced — and it says WHICH.
      // Repeating those tests on screen would be a second opinion that can
      // drift; the screen shows the database's own sentence.
      const { error: rpcErr } = await supabase.rpc('cancel_service_line', {
        p_assignment_id: assignmentId,
        p_reason: reason,
      });
      if (rpcErr) throw rpcErr;
      toast.success('Line removed. The record is in the deletion register.');
      setReason('');
      onRemoved();
      onClose();
    } catch (e) {
      setError(errorMessage(e, 'Could not remove the line.', 'remove-service-line'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center p-0 sm:items-center sm:p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-tcs-scrim/60" onClick={busy ? undefined : onClose} />
      <div className="relative z-10 w-full max-w-md rounded-t-2xl bg-tcs-bg p-5 shadow-2xl sm:rounded-2xl">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-full bg-rag-red/10 text-rag-red">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-tcs-text">Remove a line that never started</h2>
            <p className="mt-1 text-sm text-tcs-text-secondary">
              This building has not opened, so nobody has worked a night on{' '}
              <span className="font-medium text-tcs-text">{label}</span>. Removing it is not the
              same as stopping it — there is no last night to name, because there was never a first
              one.
            </p>
          </div>
        </div>

        <label className="mt-4 block text-xs font-medium text-tcs-text" htmlFor="remove-line-reason">
          Why is it being removed?
        </label>
        <textarea
          id="remove-line-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          rows={3}
          placeholder="e.g. raised twice by mistake during setup"
          className="mt-1 w-full rounded-md border border-tcs-border bg-tcs-raised px-2 py-1.5 text-sm text-tcs-text"
        />
        <p className="mt-1 text-[11px] text-tcs-text-secondary">
          The whole line is kept in the deletion register with this reason, so it can be brought
          back. The reason is the only thing that will say what was here.
        </p>

        {error && (
          <p className="mt-3 rounded-md border border-st-danger-border bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg-strong">
            {error}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Keep it
          </Button>
          <Button onClick={() => void remove()} disabled={busy || reason.trim() === ''}>
            {busy ? 'Removing…' : 'Remove the line'}
          </Button>
        </div>
      </div>
    </div>
  );
}
